// The consult's evidence, on demand, from the practitioner's own list.
//
// `SessionTimeline` was reachable in exactly two places: embedded in
// `ConsultCompleteDialog` (so only BEFORE the consult is closed out) and in the
// super-admin console. After capture — which is precisely when a client asks "why 52
// minutes?" — the practitioner had no way to see the joins, leaves and bridged gaps that
// produced the number they billed. This dialog is that way, and it is READ-ONLY by
// construction: no action, no server write, `onClose` always reports "nothing changed".
//
// It renders the SAME `SessionTimeline` as the other two surfaces rather than a
// practitioner-flavoured variant, because it is evidence: a dispute answer that differed
// by who was looking at it would answer nothing.
import React, { useEffect } from "react";
import { useT } from "@/lib/LanguageContext";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
// Relative: gateway/ has no "@/" Jest shim (see gateway/__testutils__/mocks.js) — these
// must resolve for real.
import defaultGatewayApi from "../../gateway/api";
import { useConsultSession } from "../../gateway/hooks/useConsultSession";
import SessionTimeline from "./SessionTimeline";

/**
 * @param {?string} appointmentId withheld while closed, so no read is issued
 * @param {boolean} open
 * @param {function} onClose (didMutate: boolean) => void — always false here
 * @param {object} [gatewayApi] DI seam (contract §9)
 */
export default function ConsultTimelineDialog({
  appointmentId, open, onClose, gatewayApi = defaultGatewayApi,
}) {
  const t = useT("admin");
  const { data: session, reload } = useConsultSession(appointmentId, {}, gatewayApi);

  // Re-read on every OPEN, not only when the id changes: reopening the same consult
  // after ending it must show the events that ended it, not the snapshot from before.
  useEffect(() => {
    if (open && appointmentId) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointmentId]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent data-testid="consult-timeline-dialog">
        <DialogHeader>
          <DialogTitle>{t("consult.timeline.dialogTitle")}</DialogTitle>
          <DialogDescription>{t("consult.complete.desc")}</DialogDescription>
        </DialogHeader>
        <SessionTimeline session={session} />
      </DialogContent>
    </Dialog>
  );
}
